import React from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { convertHeicIfNeeded } from '../utils/convertHeic';
import {
	Box,
	Drawer,
	AppBar,
	Toolbar,
	List,
	Typography,
	Divider,
	IconButton,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Avatar,
	Menu,
	MenuItem,
	Badge,
	InputBase,
	Paper,
	CircularProgress,
	Popover,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Tabs,
	Tab,
} from '@mui/material';
import {
	Dashboard as DashboardIcon,
	Hotel as HotelIcon,
	BookOnline as BookingIcon,
	People as PeopleIcon,
	RateReview as ReviewIcon,
	LocalOffer as VoucherIcon,
	Settings as SettingsIcon,
	Logout as LogoutIcon,
	Menu as MenuIcon,
	Search as SearchIcon,
	NotificationsNone as NotificationsIcon,
	PersonOutline as PersonIcon,
	AdminPanelSettings as AdminIcon,
	Storage as StorageIcon,
	Category as CategoryIcon,
	RoomService as AmenityIcon,
	Payments as PaymentsIcon,
	Chat as ChatIcon,
	Send as SendIcon,
	Close as CloseIcon,
	AttachFile as AttachFileIcon,
	Image as ImageIcon,
	Reply as ReplyIcon,
	DeleteOutline as DeleteOutlineIcon,
	MoreHoriz as MoreHorizIcon,
	Wallpaper as WallpaperIcon,
	Check as CheckIcon,
	Brightness4 as DarkModeIcon,
	Brightness7 as LightModeIcon,
} from '@mui/icons-material';
import Tooltip from './Tooltip';
import AdminProfileModal from './AdminProfileModal';
import ImagePreviewModal from './ImagePreviewModal';
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../context/DarkModeContext';
import { useEffect, useState, useRef } from 'react';
import { userService, chatService, settingsService, notificationService } from '../services/api';
import { useSocket } from '../context/SocketContext';
import { joinConversation, leaveConversation } from '../config/socket';

const AVATAR_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8888/api').replace('/api', '') + '/public';
const getAvatarUrl = (avatar) => {
	if (!avatar) return undefined;
	const p = typeof avatar === 'object' ? avatar.path : avatar;
	return p ? `${ AVATAR_BASE }${ p }` : undefined;
};

const drawerWidth = 250;

const menuGroups = [
	{
		label: 'TỔNG QUAN',
		items: [
			{ text: 'Thống kê', icon: <DashboardIcon fontSize="small" />, path: '/admin/dashboard' },
		],
	},
	{
		label: 'QUẢN LÝ',
		items: [
			{ text: 'Khách sạn', icon: <HotelIcon fontSize="small" />, path: '/admin/hotels' },
			{ text: 'Quản lý tour', icon: <HotelIcon fontSize="small" />, path: '/admin/tours' },
			{ text: 'Đơn đặt', icon: <BookingIcon fontSize="small" />, path: '/admin/random-bookings' },
			{ text: 'Quản lý User', icon: <PeopleIcon fontSize="small" />, path: '/admin/users' },
			{ text: 'Quản lý thu nhập', icon: <PaymentsIcon fontSize="small" />, path: '/admin/incomes' },
			// { text: 'Đánh giá', icon: <ReviewIcon fontSize="small" />, path: '/admin/reviews' },
			// { text: 'Bài viết', icon: <CategoryIcon fontSize="small" />, path: '/admin/posts' },
			// { text: 'Voucher', icon: <VoucherIcon fontSize="small" />, path: '/admin/vouchers' },
		],
	},
	{
		label: 'DANH MỤC',
		items: [
			{ text: 'Quản lý danh mục', icon: <CategoryIcon fontSize="small" />, path: '/admin/categories' },
			{ text: 'Loại giường', icon: <HotelIcon fontSize="small" />, path: '/admin/bed-types' },
			{ text: 'Loại dịch vụ', icon: <AmenityIcon fontSize="small" />, path: '/admin/extra-types' },
			{ text: 'Quản lý tiện nghi', icon: <AmenityIcon fontSize="small" />, path: '/admin/amenities' },
		],
	},
	{
		label: 'HỆ THỐNG',
		items: [
			{ text: 'Dữ liệu hệ thống', icon: <StorageIcon fontSize="small" />, path: '/admin/system-data' },
			{ text: 'Cài đặt', icon: <SettingsIcon fontSize="small" />, path: '/admin/settings' },
		],
	},
];

const CHAT_API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8888/api').replace('/api', '') + '/public';

function AdminLayout() {
	const { user, logout } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const [mobileOpen, setMobileOpen] = React.useState(false);

	// Auto-close mobile drawer on route change
	useEffect(() => {
		setMobileOpen(false);
	}, [location.pathname]);
	const [anchorEl, setAnchorEl] = React.useState(null);
	const [notificationCount, setNotificationCount] = useState(0);
	const [notifications, setNotifications] = useState([]);
	const [notifAnchorEl, setNotifAnchorEl] = useState(null);
	const [profileOpen, setProfileOpen] = useState(false);
	const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
	const { darkMode, toggle: toggleDarkMode } = useDarkMode();
	const [currentTime, setCurrentTime] = useState(new Date());

	// Site settings (logo + custom menu items)
	const [siteSettings, setSiteSettings] = useState(null);
	useEffect(() => {
		settingsService.getSettings().then((res) => setSiteSettings(res.data)).catch(() => { });
	}, []);

	const dynamicMenuGroups = React.useMemo(() => {
		if (!siteSettings?.menuItems?.length) return menuGroups;
		const groups = menuGroups.map((g) => ({ ...g, items: [...g.items] }));
		siteSettings.menuItems
			.filter((m) => m.isActive)
			// Trong admin sidebar không hiển thị nhóm "KHÁC" (chỉ dùng cho layout người dùng)
			.filter((m) => (m.group || 'KHÁC').toUpperCase() !== 'KHÁC')
			// Ẩn nhóm "KHÁCH SẠN" (menu tuỳ chỉnh) trong admin sidebar
			.filter((m) => (m.group || '').toUpperCase() !== 'KHÁCH SẠN')
			.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
			.forEach((m) => {
				const iconEl = m.icon
					? <Box component="img" src={ `${ AVATAR_BASE }${ m.icon.path }` } alt="" sx={ { width: 18, height: 18, objectFit: 'cover', borderRadius: 0.5 } } />
					: null;
				const existing = groups.find((g) => g.label === m.group);
				if (existing) {
					existing.items.push({ text: m.label, icon: iconEl, path: m.path });
				} else {
					groups.push({ label: m.group, items: [{ text: m.label, icon: iconEl, path: m.path }] });
				}
			});
		return groups;
	}, [siteSettings]);

	useEffect(() => {
		const timer = setInterval(() => setCurrentTime(new Date()), 1000);
		return () => clearInterval(timer);
	}, []);
	const formatClock = (d) => {
		const days = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
		const hh = String(d.getHours()).padStart(2, '0');
		const mm = String(d.getMinutes()).padStart(2, '0');
		const ss = String(d.getSeconds()).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		const mo = String(d.getMonth() + 1).padStart(2, '0');
		return { time: `${ hh }:${ mm }:${ ss }`, date: `${ days[d.getDay()] }, ${ dd }/${ mo }/${ d.getFullYear() }` };
	};
	const { socket } = useSocket();
	const [chatAnchorEl, setChatAnchorEl] = useState(null);
	const [chatCount, setChatCount] = useState(0);
	const [conversations, setConversations] = useState([]);
	const [loadingConversations, setLoadingConversations] = useState(false);
	const [searchUser, setSearchUser] = useState('');
	const [userResults, setUserResults] = useState([]);
	const [loadingUsers, setLoadingUsers] = useState(false);
	// Floating chat window state
	const [openChatWindow, setOpenChatWindow] = useState(false);
	const [activeChatConv, setActiveChatConv] = useState(null);
	const [imagePreview, setImagePreview] = useState(null);
	const [chatMessages, setChatMessages] = useState([]);
	const [chatInput, setChatInput] = useState('');
	const [chatSending, setChatSending] = useState(false);
	const [chatHasMore, setChatHasMore] = useState(false);
	const [chatLoadingMore, setChatLoadingMore] = useState(false);
	const [replyingTo, setReplyingTo] = useState(null);
	// Customization
	const [chatBgColor, setChatBgColor] = useState('#ffffff');
	const [ownBubbleColor, setOwnBubbleColor] = useState('#0084ff');
	const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
	const [themeModalOpen, setThemeModalOpen] = useState(false);
	const [themeTab, setThemeTab] = useState(0); // 0=Nền, 1=Bong bóng
	const [pendingBgColor, setPendingBgColor] = useState('#ffffff');
	const [pendingBgFile, setPendingBgFile] = useState(null); // File object
	const [pendingBgPreview, setPendingBgPreview] = useState(null); // data URL for preview only
	const [pendingBubbleColor, setPendingBubbleColor] = useState('#0084ff');
	const [onlineUsers, setOnlineUsers] = useState(new Set());
	const [convMenuAnchor, setConvMenuAnchor] = useState(null); // { el, conv }
	const [deleteConfirmConv, setDeleteConfirmConv] = useState(null);
	const fileInputRef = useRef(null);
	const bgInputRef = useRef(null);
	const chatEndRef = useRef(null);
	const chatScrollRef = useRef(null);
	const activeChatConvRef = useRef(null);
	const processedMsgIds = useRef(new Set());
	const shouldScrollToBottom = useRef(true);
	// sync ref so socket handler always has latest value
	activeChatConvRef.current = activeChatConv;

	// Fetch initial notifications
	useEffect(() => {
		notificationService.getAll({ limit: 20 })
			.then((res) => {
				if (res?.success) {
					setNotifications(res.data || []);
					setNotificationCount(res.unreadCount || 0);
				}
			})
			.catch(() => { });
	}, []);

	// Realtime: listen to booking:new and notification:new
	useEffect(() => {
		if (!socket) return;
		const handleNotif = (notif) => {
			setNotifications((prev) => [notif, ...prev].slice(0, 20));
			setNotificationCount((c) => c + 1);
		};
		const handleBookingNew = () => {
			// Counter already bumped via notification:new event; nothing else needed here
		};
		socket.on('notification:new', handleNotif);
		socket.on('booking:new', handleBookingNew);
		return () => {
			socket.off('notification:new', handleNotif);
			socket.off('booking:new', handleBookingNew);
		};
	}, [socket]);

	const handleNotifClick = async (notif) => {
		try {
			if (!notif.isRead) {
				await notificationService.markAsRead(notif._id);
				setNotifications((prev) => prev.map((n) => n._id === notif._id ? { ...n, isRead: true } : n));
				setNotificationCount((c) => Math.max(0, c - 1));
			}
		} catch { /* ignore */ }
		setNotifAnchorEl(null);
		if (notif.link) navigate(notif.link);
	};

	const handleMarkAllRead = async () => {
		try {
			await notificationService.markAllAsRead();
			setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
			setNotificationCount(0);
		} catch { /* ignore */ }
	};

	const openConversation = (conv) => {
		// Same conversation already open — just close the menu, don't clear anything
		if (activeChatConv?._id === conv._id && openChatWindow) {
			handleChatClose();
			return;
		}
		if (activeChatConv && activeChatConv._id !== conv._id) {
			leaveConversation(activeChatConv._id);
		}
		setActiveChatConv(conv);
		setChatMessages([]);
		setChatHasMore(false);
		setReplyingTo(null);
		// Load theme from DB
		setChatBgColor(conv.theme?.startsWith('/') ? `url(${ CHAT_API_BASE }${ conv.theme })` : conv.theme || '#ffffff');
		setOwnBubbleColor(conv.bubbleColor || '#0084ff');
		setOpenChatWindow(true);
		joinConversation(conv._id);
		handleChatClose();
	};

	const handleChatSend = async () => {
		if ((!chatInput.trim() && !fileInputRef.current?.files?.[0]) || !activeChatConv || chatSending) return;
		const content = chatInput.trim();
		const file = fileInputRef.current?.files?.[0];
		setChatInput('');
		setReplyingTo(null);
		if (fileInputRef.current) fileInputRef.current.value = '';
		setChatSending(true);
		try {
			let payload;
			if (file) {
				payload = new FormData();
				payload.append('file', file);
				if (content) payload.append('content', content);
				if (replyingTo) payload.append('replyTo', replyingTo._id);
			} else {
				payload = { content };
				if (replyingTo) payload.replyTo = replyingTo._id;
			}
			const res = await chatService.sendMessage(activeChatConv._id, payload);
			const msg = res.data;
			if (msg) {
				setChatMessages(prev => [...prev, msg]);
				setConversations(prev => prev.map(c =>
					c._id === activeChatConv._id
						? { ...c, lastMessage: msg, lastMessageAt: msg.createdAt }
						: c
				));
			}
		} catch (e) {
			setChatInput(content);
		} finally {
			setChatSending(false);
		}
	};

	const handleLoadMoreMessages = async () => {
		if (!activeChatConv || chatLoadingMore || !chatHasMore) return;
		const firstMsg = chatMessages[0];
		if (!firstMsg) return;
		setChatLoadingMore(true);
		try {
			const res = await chatService.getMessages(activeChatConv._id, { before: firstMsg._id, limit: 20 });
			const older = res.data || [];
			const hasMore = res.hasMore || false;
			// Preserve scroll position — do NOT scroll to bottom
			const container = chatScrollRef.current;
			const prevHeight = container ? container.scrollHeight : 0;
			shouldScrollToBottom.current = false;
			setChatMessages(prev => [...older, ...prev]);
			setChatHasMore(hasMore);
			if (container) {
				requestAnimationFrame(() => {
					container.scrollTop = container.scrollHeight - prevHeight;
				});
			}
		} catch (e) { }
		finally { setChatLoadingMore(false); }
	};

	const handleChatOpen = (event) => {
		setChatAnchorEl(event.currentTarget);
		fetchConversations();
	};
	const fetchConversations = () => {
		setLoadingConversations(true);
		chatService.getConversations()
			.then(res => {
				const data = res.data || [];
				setConversations(data);
				// Seed online users from server-side isOnline flags
				const onlineSet = new Set();
				data.forEach(conv => conv.participants?.forEach(p => { if (p.isOnline) onlineSet.add(p._id?.toString()); }));
				setOnlineUsers(onlineSet);
				const totalUnread = data.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
				setChatCount(totalUnread);
			})
			.catch(() => { })
			.finally(() => setLoadingConversations(false));
	};
	const handleChatClose = () => {
		setChatAnchorEl(null);
		setSearchUser('');
		setUserResults([]);
	};
	// Fetch conversations on mount & socket update
	useEffect(() => {
		fetchConversations();
	}, []);
	useEffect(() => {
		if (!socket) return;
		const handleNewMessage = (message) => {
			// Deduplicate: backend emits to both user: and conversation: rooms
			if (processedMsgIds.current.has(message._id)) return;
			processedMsgIds.current.add(message._id);
			// Keep set bounded
			if (processedMsgIds.current.size > 200) processedMsgIds.current.clear();

			const currentUserId = (user?.id || user?._id)?.toString();
			const isOwnMessage = message.sender?._id?.toString() === currentUserId
				|| message.sender?.toString() === currentUserId;

			setConversations(prev => {
				const exists = prev.find(c => c._id === message.conversation);
				let updated;
				if (exists) {
					updated = prev.map(c =>
						c._id === message.conversation
							? { ...c, lastMessage: message, lastMessageAt: message.createdAt, unreadCount: isOwnMessage ? (c.unreadCount || 0) : (c.unreadCount || 0) + 1 }
							: c
					);
				} else {
					updated = prev;
					fetchConversations();
				}
				return updated.sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
			});
			if (!isOwnMessage) {
				setChatCount(prev => prev + 1);
			}
			// Push to floating window if it's the active conversation — deduplicate by _id
			if (activeChatConvRef.current && message.conversation === activeChatConvRef.current._id && !isOwnMessage) {
				setChatMessages(prev => {
					if (prev.some(m => m._id === message._id)) return prev;
					return [...prev, message];
				});
			}
		};
		socket.on('message:new', handleNewMessage);
		const handleThemeChanged = ({ conversationId, theme, bubbleColor }) => {
			if (activeChatConvRef.current?._id === conversationId) {
				if (theme !== undefined) setChatBgColor(theme?.startsWith('/') ? `url(${ CHAT_API_BASE }${ theme })` : theme || '#ffffff');
				if (bubbleColor !== undefined) setOwnBubbleColor(bubbleColor || '#0084ff');
			}
		};
		socket.on('theme:changed', handleThemeChanged);
		const handleConversationNew = (conv) => {
			setConversations(prev => prev.some(c => c._id === conv._id) ? prev : [conv, ...prev]);
		};
		socket.on('conversation:new', handleConversationNew);
		const handleUserOnline = ({ userId }) => {
			setOnlineUsers(prev => { const next = new Set(prev); next.add(userId); return next; });
			setConversations(prev => prev.map(c => ({
				...c,
				participants: c.participants?.map(p => p._id?.toString() === userId ? { ...p, isOnline: true } : p)
			})));
		};
		socket.on('user:online', handleUserOnline);
		const handleUserOffline = ({ userId, lastSeen }) => {
			setOnlineUsers(prev => { const next = new Set(prev); next.delete(userId); return next; });
			setConversations(prev => prev.map(c => ({
				...c,
				participants: c.participants?.map(p => p._id?.toString() === userId ? { ...p, isOnline: false, lastSeen } : p)
			})));
		};
		socket.on('user:offline', handleUserOffline);
		const handleMessageRecalled = ({ messageId, conversationId, newLastMessage }) => {
			setChatMessages(prev => prev.filter(m => m._id !== messageId));
			setConversations(prev => prev.map(c =>
				c._id === conversationId && c.lastMessage?._id === messageId
					? { ...c, lastMessage: newLastMessage || null }
					: c
			));
		};
		socket.on('message:recalled', handleMessageRecalled);
		const handleConversationDeleted = ({ conversationId }) => {
			setConversations(prev => prev.filter(c => c._id !== conversationId));
			setActiveChatConv(cur => cur?._id === conversationId ? null : cur);
		};
		socket.on('conversation:deleted', handleConversationDeleted);
		return () => {
			socket.off('message:new', handleNewMessage);
			socket.off('theme:changed', handleThemeChanged);
			socket.off('conversation:new', handleConversationNew);
			socket.off('user:online', handleUserOnline);
			socket.off('user:offline', handleUserOffline);
			socket.off('message:recalled', handleMessageRecalled);
			socket.off('conversation:deleted', handleConversationDeleted);
		};
	}, [socket, user]);
	// Fetch messages khi mở cửa sổ chat
	useEffect(() => {
		if (!activeChatConv || !openChatWindow) return;
		chatService.getMessages(activeChatConv._id, { limit: 20 })
			.then(res => {
				setChatMessages(res.data || []);
				setChatHasMore(res.hasMore || false);
			})
			.catch(() => { });
	}, [activeChatConv?._id, openChatWindow]); // eslint-disable-line react-hooks/exhaustive-deps
	// Scroll to bottom khi có tin mới (không scroll khi load older)
	useEffect(() => {
		if (shouldScrollToBottom.current && chatScrollRef.current) {
			const el = chatScrollRef.current;
			requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
		}
		shouldScrollToBottom.current = true;
	}, [chatMessages]);
	// Lock body scroll on mobile when chat window is open
	useEffect(() => {
		const isMobile = window.innerWidth < 600;
		if (!openChatWindow || !isMobile) return;
		const savedScrollY = window.scrollY;
		document.body.style.position = 'fixed';
		document.body.style.top = `-${savedScrollY}px`;
		document.body.style.width = '100%';
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.width = '';
			document.body.style.overflow = '';
			window.scrollTo(0, savedScrollY);
		};
	}, [openChatWindow]);
	// Tìm kiếm user
	useEffect(() => {
		if (searchUser.trim().length === 0) {
			setUserResults([]);
			setLoadingUsers(false);
			return;
		}
		let cancelled = false;
		const timer = setTimeout(() => {
			setLoadingUsers(true);
			userService.getAll({ search: searchUser.trim(), limit: 10 })
				.then(res => {
					if (!cancelled) {
						setUserResults(res.data || []);
						setLoadingUsers(false);
					}
				})
				.catch(() => {
					if (!cancelled) {
						setUserResults([]);
						setLoadingUsers(false);
					}
				});
		}, 300);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [searchUser]);
	// Tạo cuộc trò chuyện mới
	const handleCreateConversation = (userId) => {
		chatService.getOrCreateDirect(userId).then((res) => {
			const newConv = res.data;
			if (newConv) {
				setConversations(prev => {
					const exists = prev.find(c => c._id === newConv._id);
					return exists ? prev : [newConv, ...prev];
				});
				openConversation(newConv);
			}
			setSearchUser('');
			setUserResults([]);
		});
	};

	// Xóa đoạn chat ở phía bản thân
	const handleDeleteConversation = async (convId) => {
		try {
			await chatService.deleteConversation(convId);
			setConversations(prev => prev.filter(c => c._id !== convId));
			if (activeChatConvRef.current?._id === convId) {
				leaveConversation(convId);
				setOpenChatWindow(false);
				setActiveChatConv(null);
				setChatMessages([]);
			}
		} catch (e) { }
		setDeleteConfirmConv(null);
	};

	const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
	const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
	const handleMenuClose = () => setAnchorEl(null);
	const handleLogout = () => { handleMenuClose(); setLogoutConfirmOpen(true); };
	const confirmLogout = async () => { setLogoutConfirmOpen(false); await logout(); navigate('/login'); };

	const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

	const drawer = (
		<Box sx={ { height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' } }>
			{/* Logo / Brand */ }
			<Box
				sx={ {
					px: 3,
					py: 2.5,
					display: 'flex',
					alignItems: 'center',
					gap: 1.5,
					borderBottom: '1px solid',
					borderColor: 'divider',
					minHeight: 64,
				} }
			>
				<Box
					sx={ {
						width: siteSettings?.logo ? '100%' : 36,
						height: 44,
						borderRadius: 2,
						bgcolor: siteSettings?.logo ? 'transparent' : 'primary.main',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						overflow: 'hidden',
					} }
				>
					{ siteSettings?.logo ? (
						<Box
							component="img"
							src={ `${ AVATAR_BASE }${ siteSettings.logo.path }` }
							alt="logo"
							sx={ { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' } }
						/>
					) : (
						<AdminIcon sx={ { color: '#fff', fontSize: 20 } } />
					) }
				</Box>
				{ !siteSettings?.logo && (
					<Box>
						<Typography variant="subtitle1" fontWeight={ 700 } color="primary.main" lineHeight={ 1.2 }>
							TravelAdmin
						</Typography>
						<Typography variant="caption" color="text.secondary">
							Quản trị hệ thống
						</Typography>
					</Box>
				) }
			</Box>

			{/* Menu */ }
			<Box sx={ { flex: 1, overflowY: 'auto', py: 1.5 } }>
				{ dynamicMenuGroups.map((group) => (
					<Box key={ group.label } sx={ { mb: 1 } }>
						<Typography
							variant="caption"
							sx={ {
								px: 3,
								py: 0.5,
								display: 'block',
								color: 'text.disabled',
								fontWeight: 600,
								letterSpacing: '0.08em',
								fontSize: '0.68rem',
							} }
						>
							{ group.label }
						</Typography>
						<List disablePadding>
							{ group.items.map((item) => {
								const active = isActive(item.path);
								return (
									<ListItem key={ item.text } disablePadding sx={ { px: 1.5, mb: 0.25 } }>
										<ListItemButton
											component={ NavLink }
											to={ item.path }
											sx={ {
												borderRadius: 2,
												px: 1.5,
												py: 0.85,
												bgcolor: active ? 'primary.main' : 'transparent',
												color: active ? '#fff' : 'text.secondary',
												fontWeight: active ? 600 : 400,
												'& .MuiListItemIcon-root': {
													color: active ? '#fff' : 'text.disabled',
													minWidth: 36,
												},
												'&:hover': {
													bgcolor: active ? 'primary.dark' : 'action.hover',
													color: active ? '#fff' : 'primary.main',
													'& .MuiListItemIcon-root': { color: active ? '#fff' : 'primary.main' },
												},
												transition: 'all 0.15s ease',
											} }
										>
											{ active && (
												<Box
													sx={ {
														position: 'absolute',
														left: 0,
														top: '20%',
														bottom: '20%',
														width: 3,
														borderRadius: '0 4px 4px 0',
														bgcolor: active ? '#fff' : 'primary.main',
													} }
												/>
											) }
											<ListItemIcon>{ item.icon }</ListItemIcon>
											<ListItemText
												primary={ item.text }
												primaryTypographyProps={ { fontSize: '0.875rem', fontWeight: active ? 600 : 400 } }
											/>
										</ListItemButton>
									</ListItem>
								);
							}) }
						</List>
					</Box>
				)) }
			</Box>

			{/* User bottom */ }
			<Box sx={ { borderTop: '1px solid', borderColor: 'divider', p: 2 } }>
				<Box sx={ { display: 'flex', alignItems: 'center', gap: 1.5 } }>
					<Avatar
						alt={ user?.fullName || user?.username }
						src={ getAvatarUrl(user?.avatar) }
						sx={ { width: 36, height: 36, bgcolor: 'primary.main', fontSize: 15 } }
					>
						{ user?.fullName?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase() }
					</Avatar>
					<Box sx={ { flex: 1, minWidth: 0 } }>
						<Typography variant="body2" fontWeight={ 600 } noWrap>{ user?.fullName || user?.username }</Typography>
						<Typography variant="caption" color="text.secondary" noWrap>{ user?.email }</Typography>
					</Box>
					<Tooltip title="Đăng xuất">
						<IconButton size="small" onClick={ handleLogout } sx={ { color: 'text.disabled', '&:hover': { color: 'error.main' } } }>
							<LogoutIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>
		</Box>
	);

	return (
		<Box sx={ { display: 'flex', bgcolor: 'background.default', minHeight: '100vh' } }>
			{/* Top AppBar */ }
			<AppBar
				position="fixed"
				elevation={ 0 }
				sx={ {
					width: { sm: `calc(100% - ${ drawerWidth }px)` },
					ml: { sm: `${ drawerWidth }px` },
					bgcolor: 'background.paper',
					borderBottom: '1px solid', borderColor: 'divider',
					color: 'text.primary',
				} }
			>
				<Toolbar sx={ { gap: { xs: 0.25, sm: 1 }, px: { xs: 1, sm: 3 }, minHeight: { xs: 56, sm: 64 } } }>
					<IconButton
						edge="start"
						onClick={ handleDrawerToggle }
						sx={ { mr: { xs: 0.5, sm: 1 }, display: { sm: 'none' } } }
					>
						<MenuIcon />
					</IconButton>

					{/* Clock */ }
					<Box sx={ { display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.5 }, minWidth: 0 } }>
						<Typography sx={ { fontSize: { xs: 14, sm: 17 }, fontWeight: 700, letterSpacing: { xs: 0.3, sm: 1 }, color: 'text.primary', fontVariantNumeric: 'tabular-nums', lineHeight: 1 } }>
							{ formatClock(currentTime).time }
						</Typography>
						<Typography sx={ { fontSize: 13, color: 'text.secondary', lineHeight: 1, display: { xs: 'none', md: 'block' } } }>
							{ formatClock(currentTime).date }
						</Typography>
					</Box>

					<Box sx={ { flex: 1 } } />


					{/* Chat */ }
					<IconButton sx={ { color: 'text.secondary' } } onClick={ handleChatOpen }>
						<Badge badgeContent={ chatCount } color="error">
							<ChatIcon />
						</Badge>
					</IconButton>
					<Menu
						anchorEl={ chatAnchorEl }
						open={ Boolean(chatAnchorEl) }
						onClose={ handleChatClose }
						anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } }
						transformOrigin={ { vertical: 'top', horizontal: 'right' } }
						PaperProps={ { sx: { width: { xs: 'calc(100vw - 16px)', sm: 340 }, maxWidth: { xs: 'calc(100vw - 16px)', sm: 340 }, maxHeight: { xs: '70vh', sm: 500 }, p: 0, bgcolor: 'background.paper' } } }
					>
						<Box sx={ { px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' } }>
							<Typography fontWeight={ 600 }>Tin nhắn</Typography>
						</Box>
						<Box sx={ { px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 } }>
							<SearchIcon sx={ { color: 'text.disabled', fontSize: 20 } } />
							<InputBase
								placeholder="Tìm kiếm người dùng..."
								value={ searchUser }
								onChange={ e => setSearchUser(e.target.value) }
								sx={ { fontSize: 14, width: '100%' } }
								autoFocus
							/>
						</Box>
						{ searchUser.trim() ? (
							loadingUsers ? (
								<Box sx={ { p: 2, color: '#888' } }>Đang tìm kiếm...</Box>
							) : userResults.length === 0 ? (
								<Box sx={ { p: 2, color: '#888' } }>Không tìm thấy người dùng.</Box>
							) : (
								userResults.map(u => (
									<MenuItem key={ u._id } onClick={ () => handleCreateConversation(u._id) } sx={ { alignItems: 'flex-start', gap: 1.5 } }>
										<Avatar sx={ { width: 36, height: 36, fontSize: 15 } } src={ getAvatarUrl(u.avatar) }>{ u.fullName?.charAt(0) || u.username?.charAt(0) }</Avatar>
										<Box sx={ { flex: 1, minWidth: 0 } }>
											<Typography fontWeight={ 600 } fontSize={ 14 } noWrap>{ u.fullName || u.username }</Typography>
											<Typography fontSize={ 13 } color="text.secondary" noWrap>{ u.email }</Typography>
										</Box>
									</MenuItem>
								))
							)
						) : (
							loadingConversations ? (
								<Box sx={ { p: 2, textAlign: 'center', color: '#888' } }>Đang tải...</Box>
							) : conversations.length === 0 ? (
								<Box sx={ { p: 2, color: '#888' } }>Không có hội thoại nào.</Box>
							) : (
								conversations.map(conv => {
									const currentUserId = (user?.id || user?._id)?.toString();
									const other = conv.participants?.find(p => p._id?.toString() !== currentUserId);
									const isOwnLast = conv.lastMessage?.sender?._id?.toString() === currentUserId
										|| conv.lastMessage?.sender?.toString() === currentUserId;
									const lastMsgType = conv.lastMessage?.type || conv.lastMessage?.attachments?.[0]?.type;
									const lastMsgContent = conv.lastMessage?.recalled ? ''
										: lastMsgType === 'image' ? 'Đã gửi ảnh'
											: lastMsgType === 'file' ? '📎 Đã gửi file'
												: (conv.lastMessage?.content || '');
									const lastText = (conv.lastMessage && lastMsgContent)
										? (isOwnLast ? `Bạn: ${ lastMsgContent }` : lastMsgContent)
										: 'Chưa có tin nhắn';
									return (
										<MenuItem key={ conv._id } onClick={ () => openConversation(conv) } sx={ { alignItems: 'flex-start', gap: 1.5, py: 1.2, '&:hover .conv-more-btn': { opacity: 1 } } }>
											<Box sx={ { position: 'relative', flexShrink: 0 } }>
												<Avatar sx={ { width: 36, height: 36, fontSize: 15 } } src={ getAvatarUrl(other?.avatar) }>
													{ other?.fullName?.charAt(0) || other?.username?.charAt(0) }
												</Avatar>
												{ (other?.isOnline || onlineUsers.has(other?._id?.toString())) && (
													<Box sx={ { position: 'absolute', bottom: 0, right: 0, width: 11, height: 11, borderRadius: '50%', bgcolor: '#44b700', border: '2.5px solid', borderColor: 'background.paper' } } />
												) }
											</Box>
											<Box sx={ { flex: 1, minWidth: 0 } }>
												<Typography fontWeight={ 600 } fontSize={ 14 } noWrap>{ other?.fullName || other?.username }</Typography>
												<Typography fontSize={ 13 } color="text.secondary" noWrap>{ lastText }</Typography>
											</Box>
											{ (conv.unreadCount || 0) > 0 && (
												<Badge color="error" badgeContent={ conv.unreadCount } sx={ { ml: 1, alignSelf: 'center' } } />
											) }
											<IconButton
												className="conv-more-btn"
												size="small"
												onClick={ (e) => { e.stopPropagation(); setConvMenuAnchor({ el: e.currentTarget, conv }); } }
												sx={ { opacity: 0, transition: 'opacity 0.15s', alignSelf: 'center', p: 0.5 } }
											>
												<MoreHorizIcon fontSize="small" />
											</IconButton>
										</MenuItem>
									);
								})
							)
						) }
					</Menu>

					{/* Conversation context menu (xóa đoạn chat) */ }
					<Menu
						anchorEl={ convMenuAnchor?.el }
						open={ Boolean(convMenuAnchor) }
						onClose={ () => setConvMenuAnchor(null) }
						anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } }
						transformOrigin={ { vertical: 'top', horizontal: 'right' } }
						PaperProps={ { sx: { minWidth: 180 } } }
					>
						<MenuItem onClick={ () => { setDeleteConfirmConv(convMenuAnchor.conv); setConvMenuAnchor(null); } } sx={ { color: 'error.main' } }>
							<DeleteOutlineIcon sx={ { mr: 1.5, fontSize: 18 } } /> Xóa đoạn chat
						</MenuItem>
					</Menu>

					{/* Confirm delete conversation */ }
					<Dialog open={ Boolean(deleteConfirmConv) } onClose={ () => setDeleteConfirmConv(null) } maxWidth="xs" fullWidth>
						<DialogTitle>Xóa đoạn chat?</DialogTitle>
						<DialogContent>
							<Typography fontSize={ 14 } color="text.secondary">
								Xác nhận xóa đoạn chat
							</Typography>
						</DialogContent>
						<DialogActions sx={ { px: 3, pb: 2 } }>
							<Button onClick={ () => setDeleteConfirmConv(null) }>Hủy</Button>
							<Button onClick={ () => handleDeleteConversation(deleteConfirmConv._id) } color="error" variant="contained">Xóa</Button>
						</DialogActions>
					</Dialog>

					{/* Notifications */ }
					<Tooltip title={ darkMode ? 'Chế độ sáng' : 'Chế độ tối' }>
						<IconButton sx={ { color: 'text.secondary' } } onClick={ toggleDarkMode }>
							{ darkMode ? <LightModeIcon /> : <DarkModeIcon /> }
						</IconButton>
					</Tooltip>
					<Tooltip title="Thông báo">
						<IconButton sx={ { color: 'text.secondary' } } onClick={ (e) => setNotifAnchorEl(e.currentTarget) }>
							<Badge badgeContent={ notificationCount } color="error">
								<NotificationsIcon />
							</Badge>
						</IconButton>
					</Tooltip>
					<Popover
						open={ Boolean(notifAnchorEl) }
						anchorEl={ notifAnchorEl }
						onClose={ () => setNotifAnchorEl(null) }
						anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } }
						transformOrigin={ { vertical: 'top', horizontal: 'right' } }
						PaperProps={ { sx: { width: 380, maxHeight: 480, mt: 1, borderRadius: 2 } } }
					>
						<Box sx={ { px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' } }>
							<Typography fontWeight={ 700 }>Thông báo</Typography>
							{ notificationCount > 0 && (
								<Button size="small" onClick={ handleMarkAllRead } sx={ { textTransform: 'none' } }>
									Đánh dấu tất cả đã đọc
								</Button>
							) }
						</Box>
						<Box sx={ { maxHeight: 400, overflow: 'auto' } }>
							{ notifications.length === 0 ? (
								<Box sx={ { p: 4, textAlign: 'center', color: 'text.secondary' } }>
									<NotificationsIcon sx={ { fontSize: 40, opacity: 0.4, mb: 1 } } />
									<Typography variant="body2">Chưa có thông báo nào</Typography>
								</Box>
							) : notifications.map((notif) => (
								<Box
									key={ notif._id }
									onClick={ () => handleNotifClick(notif) }
									sx={ {
										px: 2, py: 1.25, cursor: 'pointer',
										borderBottom: '1px solid', borderColor: 'divider',
										bgcolor: notif.isRead ? 'transparent' : 'action.hover',
										'&:hover': { bgcolor: 'action.selected' },
										display: 'flex', gap: 1.5, alignItems: 'flex-start'
									} }
								>
									<Box sx={ {
										width: 8, height: 8, borderRadius: '50%', mt: 1,
										bgcolor: notif.isRead ? 'transparent' : 'primary.main',
										flexShrink: 0,
									} } />
									<Box sx={ { minWidth: 0, flex: 1 } }>
										<Typography variant="body2" fontWeight={ notif.isRead ? 400 : 600 } noWrap>
											{ notif.title }
										</Typography>
										<Typography variant="caption" color="text.secondary" sx={ {
											display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
										} }>
											{ notif.content }
										</Typography>
										<Typography variant="caption" color="text.disabled" display="block" sx={ { mt: 0.5 } }>
											{ new Date(notif.createdAt).toLocaleString('vi-VN') }
										</Typography>
									</Box>
								</Box>
							)) }
						</Box>
					</Popover>

					{/* User avatar */ }
					<Tooltip title={ user?.fullName || user?.username }>
						<IconButton onClick={ handleMenuOpen } sx={ { p: 0.5 } }>
							<Avatar
								alt={ user?.fullName || user?.username }
								src={ getAvatarUrl(user?.avatar) }
								sx={ { width: 34, height: 34, bgcolor: 'primary.main', fontSize: 14 } }
							>
								{ user?.fullName?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase() }
							</Avatar>
						</IconButton>
					</Tooltip>
					<Menu anchorEl={ anchorEl } open={ Boolean(anchorEl) } onClose={ handleMenuClose }
						transformOrigin={ { horizontal: 'right', vertical: 'top' } }
						anchorOrigin={ { horizontal: 'right', vertical: 'bottom' } }
					>
						<Box sx={ { px: 2, py: 1 } }>
							<Typography variant="body2" fontWeight={ 600 }>{ user?.fullName || user?.username }</Typography>
							<Typography variant="caption" color="text.secondary">{ user?.email }</Typography>
						</Box>
						<Divider />
						<MenuItem onClick={ () => { setProfileOpen(true); handleMenuClose(); } }>
							<PersonIcon sx={ { mr: 1.5, fontSize: 18 } } /> Hồ sơ
						</MenuItem>
						<MenuItem onClick={ handleLogout } sx={ { color: 'error.main' } }>
							<LogoutIcon sx={ { mr: 1.5, fontSize: 18 } } /> Đăng xuất
						</MenuItem>
					</Menu>
				</Toolbar>
			</AppBar>

			{/* Sidebar nav */ }
			<Box component="nav" sx={ { width: { sm: drawerWidth }, flexShrink: { sm: 0 } } }>
				<Drawer
					variant="temporary"
					open={ mobileOpen }
					onClose={ handleDrawerToggle }
					sx={ {
						display: { xs: 'block', sm: 'none' },
						'& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none', bgcolor: 'background.paper' },
					} }
				>
					{ drawer }
				</Drawer>
				<Drawer
					variant="permanent"
					sx={ {
						display: { xs: 'none', sm: 'block' },
						'& .MuiDrawer-paper': {
							boxSizing: 'border-box',
							width: drawerWidth,
							border: 'none',
							borderRight: '1px solid', borderColor: 'divider',
						},
					} }
					open
				>
					{ drawer }
				</Drawer>
			</Box>

			{/* Main content */ }
			<Box
				component="main"
				sx={ {
					flexGrow: 1,
					p: { xs: 1.5, sm: 3 },
					width: { xs: '100%', sm: `calc(100% - ${ drawerWidth }px)` },
					mt: { xs: 7, sm: 8 },
					bgcolor: 'background.default',
					overflowX: 'hidden',
				} }
			>
				<Outlet />
			</Box>

			{/* Floating Chat Window */ }
			{ openChatWindow && activeChatConv && (() => {
				const currentUserId = (user?.id || user?._id)?.toString();
				const other = activeChatConv.participants?.find(p => p._id?.toString() !== currentUserId);
				// CHAT_API_BASE is defined at module level
				const lightenColor = (hex, amt = 0.82) => {
					if (!hex || !hex.startsWith('#') || hex.length < 7) return '#e9e9eb';
					const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
					return `rgb(${ Math.round(r + (255 - r) * amt) },${ Math.round(g + (255 - g) * amt) },${ Math.round(b + (255 - b) * amt) })`;
				};
				const otherBubbleColor = lightenColor(ownBubbleColor, 0.82);
				const datePillColor = lightenColor(ownBubbleColor, 0.72);
				const formatTime = (date) => {
					const d = new Date(date);
					return `${ String(d.getHours()).padStart(2, '0') }:${ String(d.getMinutes()).padStart(2, '0') }`;
				};
				const formatDateLabel = (date) => {
					const d = new Date(date);
					const days = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
					return `${ days[d.getDay()] } ${ String(d.getDate()).padStart(2, '0') }/${ String(d.getMonth() + 1).padStart(2, '0') }/${ d.getFullYear() }`;
				};
				return (
					<Paper elevation={ 6 } sx={ { position: 'fixed', bottom: { xs: 0, sm: 0 }, right: { xs: 0, sm: 24 }, left: { xs: 0, sm: 'auto' }, top: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 320 }, height: { xs: '100%', sm: 480 }, zIndex: 1400, display: 'flex', flexDirection: 'column', borderRadius: { xs: 0, sm: '12px 12px 0 0' }, overflow: 'hidden' } }>
						{/* Header */ }
						<Box sx={ { bgcolor: ownBubbleColor, color: '#fff', px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1 } }>
							<Box sx={ { position: 'relative', flexShrink: 0 } }>
								<Avatar src={ getAvatarUrl(other?.avatar) } sx={ { width: 30, height: 30, fontSize: 12 } }>
									{ other?.fullName?.charAt(0) || other?.username?.charAt(0) }
								</Avatar>
								{ (other?.isOnline || onlineUsers.has(other?._id?.toString())) && (
									<Box sx={ { position: 'absolute', bottom: 0, right: 0, width: 9, height: 9, borderRadius: '50%', bgcolor: '#44b700', border: '2px solid', borderColor: ownBubbleColor } } />
								) }
							</Box>
							<Box sx={ { flex: 1, minWidth: 0 } }>
								<Typography fontWeight={ 700 } fontSize={ 13 } noWrap>{ other?.fullName || other?.username }</Typography>
								{ (other?.isOnline || onlineUsers.has(other?._id?.toString())) ? (
									<Box sx={ { display: 'flex', alignItems: 'center', gap: 0.5 } }>
										<Box sx={ { width: 7, height: 7, borderRadius: '50%', bgcolor: '#44b700', flexShrink: 0 } } />
										<Typography fontSize={ 10 } sx={ { color: 'rgba(255,255,255,0.85)', lineHeight: 1 } }>Đang hoạt động</Typography>
									</Box>
								) : other?.lastSeen ? (
									<Typography fontSize={ 10 } sx={ { color: 'rgba(255,255,255,0.7)', lineHeight: 1 } }>{ (() => { const d = new Date(other.lastSeen); const diff = Date.now() - d; if (diff < 60000) return 'Hoạt động vừa xong'; if (diff < 3600000) return `Hoạt động ${ Math.floor(diff / 60000) } phút trước`; if (diff < 86400000) return `Hoạt động ${ Math.floor(diff / 3600000) } giờ trước`; return `Hoạt động ${ d.getDate() }/${ d.getMonth() + 1 }`; })() }</Typography>
								) : null }
							</Box>
							<IconButton size="small" onClick={ e => setSettingsAnchorEl(e.currentTarget) } sx={ { color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } } }>
								<MoreHorizIcon fontSize="small" />
							</IconButton>
							<IconButton size="small" onClick={ () => { leaveConversation(activeChatConv._id); setOpenChatWindow(false); } } sx={ { color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } } }>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Box>

						{/* Settings dropdown */ }
						<Menu anchorEl={ settingsAnchorEl } open={ Boolean(settingsAnchorEl) } onClose={ () => setSettingsAnchorEl(null) } anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } } transformOrigin={ { vertical: 'top', horizontal: 'right' } } PaperProps={ { sx: { minWidth: 200 } } } sx={ { zIndex: 1600 } }>
							<MenuItem onClick={ () => { setPendingBgColor(chatBgColor); setPendingBgPreview(chatBgColor.startsWith('/') ? null : null); setPendingBubbleColor(ownBubbleColor); setThemeTab(0); setThemeModalOpen(true); setSettingsAnchorEl(null); } }>
								<WallpaperIcon sx={ { mr: 1.5, fontSize: 18, color: '#555' } } /> Chủ đề &amp; màu sắc
							</MenuItem>
							<MenuItem onClick={ () => { setDeleteConfirmConv(activeChatConv); setSettingsAnchorEl(null); } } sx={ { color: 'error.main' } }>
								<DeleteOutlineIcon sx={ { mr: 1.5, fontSize: 18 } } /> Xóa đoạn chat
							</MenuItem>
						</Menu>

						{/* Unified theme modal */ }
						{ (() => {
							const CHAT_API_BASE_MODAL = CHAT_API_BASE;
							const BG_THEMES = [
								{ label: 'Mặc định', value: '#ffffff', sub: 'Trắng sạch, rõ ràng' },
								{ label: 'Bầu trời', value: '#e3f2fd', sub: 'Xanh nhạt yên bình' },
								{ label: 'Thảo nguyên', value: '#e8f5e9', sub: 'Xanh lá tươi mát' },
								{ label: 'Hoàng hôn', value: '#fce4ec', sub: 'Hồng lãng mạn' },
								{ label: 'Nắng vui', value: '#fff8e1', sub: 'Vàng ấm áp' },
								{ label: 'Lavender', value: '#f3e5f5', sub: 'Tím nhẹ nhàng' },
								{ label: 'Xuyên đêm', value: '#1a1a2e', sub: 'Xanh đậm huyền bí' },
								{ label: 'Dương nắng', value: '#fff3e0', sub: 'Cam ấm nhẹ' },
								{ label: 'Sương sớm', value: '#f0f4f8', sub: 'Xám sương mù nhẹ' },
								{ label: 'Bãi biển', value: '#e0f7fa', sub: 'Xanh biển tươi mát' },
								{ label: 'Đất nung', value: '#fbe9e7', sub: 'Đỏ gạch ấm cúng' },
								{ label: 'Kem dừa', value: '#fafafa', sub: 'Trắng kem thư giãn' },
							];
							const BUBBLE_THEMES = [
								{ label: 'Messenger', value: '#0084ff', sub: 'Xanh Facebook' },
								{ label: 'Vịt máu', value: '#e91e63', sub: 'Hồng mạnh mẽ' },
								{ label: 'Opal', value: '#9c27b0', sub: 'Tím huyền bí' },
								{ label: 'Biển khơi', value: '#0288d1', sub: 'Xanh biển sâu' },
								{ label: 'Cánh lá', value: '#388e3c', sub: 'Xanh lá tươi' },
								{ label: 'Hoàng kỳ', value: '#f57c00', sub: 'Cam rực rỡ' },
								{ label: 'Thạch lựu', value: '#c62828', sub: 'Đỏ đậm sắc' },
								{ label: 'Bạc xanh', value: '#546e7a', sub: 'Xám xửanh sang' },
								{ label: 'Gold', value: '#f9a825', sub: 'Vàng quý phái' },
								{ label: 'Nội tro', value: '#455a64', sub: 'Xám xẩm tối giản' },
								{ label: 'Tourmaline', value: '#00897b', sub: 'Xanh ngọc' },
								{ label: 'Đen tuyền', value: '#212121', sub: 'Tối giản mạnh mẽ' },
							];
							const previewBg = pendingBgPreview
								? `url(${ pendingBgPreview }) center/cover no-repeat`
								: pendingBgColor.startsWith('/') ? `url(${ CHAT_API_BASE_MODAL }${ pendingBgColor }) center/cover no-repeat`
									: pendingBgColor;
							return (
								<Dialog open={ themeModalOpen } onClose={ () => setThemeModalOpen(false) } maxWidth="md" fullWidth
									PaperProps={ { sx: { borderRadius: { xs: 0, sm: 2 }, height: { xs: '100%', sm: 580 }, m: { xs: 0, sm: 4 }, maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' } } } }
									sx={ { zIndex: 1700 } }
								>
									<DialogTitle sx={ { fontWeight: 700, fontSize: 16, borderBottom: '1px solid', borderColor: 'divider', pb: 1.5, display: 'flex', alignItems: 'center' } }>
										<span style={ { flex: 1 } }>Xem trước và chọn chủ đề</span>
										<IconButton size="small" onClick={ () => setThemeModalOpen(false) } sx={ { color: 'text.secondary' } }><CloseIcon fontSize="small" /></IconButton>
									</DialogTitle>
									<DialogContent sx={ { p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }>
										<Tabs value={ themeTab } onChange={ (_, v) => setThemeTab(v) } sx={ { borderBottom: '1px solid', borderColor: 'divider', px: 2, minHeight: 40 } } TabIndicatorProps={ { style: { height: 2 } } }>
											<Tab label="Ảnh nền" sx={ { minHeight: 40, fontSize: 13 } } />
											<Tab label="Bong bóng chat" sx={ { minHeight: 40, fontSize: 13 } } />
										</Tabs>
										<Box sx={ { display: 'flex', flex: 1, overflow: 'hidden', flexDirection: { xs: 'column', sm: 'row' } } }>
											{/* Left panel */ }
											<Box sx={ { width: { xs: '100%', sm: 260 }, height: { xs: '50%', sm: 'auto' }, borderRight: { xs: 'none', sm: '1px solid #eee' }, borderBottom: { xs: '1px solid #eee', sm: 'none' }, overflowY: 'auto', flexShrink: 0 } }>
												{ themeTab === 0 ? (
													<>
														{/* Image upload */ }
														<Box sx={ { p: 1.5, borderBottom: '1px solid #f0f0f0' } }>
															<input ref={ bgInputRef } type="file" accept="image/*" style={ { display: 'none' } }
																onChange={ e => {
																	const f = e.target.files[0];
																	if (!f) return;
																	setPendingBgFile(f);
																	const reader = new FileReader();
																	reader.onload = ev => { setPendingBgPreview(ev.target.result); setPendingBgColor('__image__'); };
																	reader.readAsDataURL(f);
																} }
															/>
															<Box onClick={ () => bgInputRef.current?.click() }
																sx={ { display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderRadius: 2, bgcolor: '#f5f5f5', cursor: 'pointer', '&:hover': { bgcolor: '#e8e8e8' } } }>
																<WallpaperIcon sx={ { fontSize: 20, color: '#555' } } />
																<Box>
																	<Typography fontSize={ 13 } fontWeight={ 500 }>Chọn ảnh từ máy tính</Typography>
																	{ pendingBgFile && <Typography fontSize={ 11 } color="text.secondary" noWrap sx={ { maxWidth: 140 } }>{ pendingBgFile.name }</Typography> }
																</Box>
															</Box>
														</Box>
														{/* Color list */ }
														{ BG_THEMES.map(opt => (
															<Box key={ opt.value } onClick={ () => { setPendingBgColor(opt.value); setPendingBgFile(null); setPendingBgPreview(null); } }
																sx={ { display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, cursor: 'pointer', bgcolor: pendingBgColor === opt.value ? 'rgba(24,119,242,0.08)' : 'transparent', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } } }>
																<Box sx={ { width: 36, height: 36, borderRadius: '50%', bgcolor: opt.value, border: '2px solid #ddd', flexShrink: 0 } } />
																<Box sx={ { flex: 1, minWidth: 0 } }>
																	<Typography fontSize={ 13 } fontWeight={ 500 }>{ opt.label }</Typography>
																	<Typography fontSize={ 11 } color="text.secondary">{ opt.sub }</Typography>
																</Box>
																{ pendingBgColor === opt.value && !pendingBgFile && <CheckIcon sx={ { fontSize: 16, color: '#1877f2' } } /> }
															</Box>
														)) }
														{/* Custom color */ }
														<Box sx={ { px: 2, py: 1.2, borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1.5 } }>
															<Box component="input" type="color"
																value={ pendingBgColor.startsWith('#') ? pendingBgColor : '#ffffff' }
																onChange={ e => { setPendingBgColor(e.target.value); setPendingBgFile(null); setPendingBgPreview(null); } }
																style={ { width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 } }
															/>
															<Box sx={ { flex: 1 } }>
																<Typography fontSize={ 13 } fontWeight={ 500 }>Tùy chỉnh</Typography>
																<Typography fontSize={ 11 } color="text.secondary">Chọn bất kỳ màu nào</Typography>
															</Box>
														</Box>
													</>
												) : (
													<>
														{ BUBBLE_THEMES.map(opt => (
															<Box key={ opt.value } onClick={ () => setPendingBubbleColor(opt.value) }
																sx={ { display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, cursor: 'pointer', bgcolor: pendingBubbleColor === opt.value ? 'rgba(24,119,242,0.08)' : 'transparent', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } } }>
																<Box sx={ { width: 36, height: 36, borderRadius: '50%', bgcolor: opt.value, flexShrink: 0 } } />
																<Box sx={ { flex: 1, minWidth: 0 } }>
																	<Typography fontSize={ 13 } fontWeight={ 500 }>{ opt.label }</Typography>
																	<Typography fontSize={ 11 } color="text.secondary">{ opt.sub }</Typography>
																</Box>
																{ pendingBubbleColor === opt.value && <CheckIcon sx={ { fontSize: 16, color: '#1877f2' } } /> }
															</Box>
														)) }
														{/* Custom bubble color */ }
														<Box sx={ { px: 2, py: 1.2, borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1.5 } }>
															<Box component="input" type="color"
																value={ pendingBubbleColor }
																onChange={ e => setPendingBubbleColor(e.target.value) }
																style={ { width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 } }
															/>
															<Box sx={ { flex: 1 } }>
																<Typography fontSize={ 13 } fontWeight={ 500 }>Tùy chỉnh</Typography>
																<Typography fontSize={ 11 } color="text.secondary">Chọn bất kỳ màu nào</Typography>
															</Box>
														</Box>
													</>
												) }
											</Box>
											{/* Right panel: preview */ }
											<Box sx={ { flex: 1, p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', background: previewBg, transition: 'background 0.3s' } }>
												<Box sx={ { display: 'flex', justifyContent: 'flex-end' } }>
													<Box sx={ { bgcolor: pendingBubbleColor, color: '#fff', px: 1.5, py: 0.75, borderRadius: '18px 18px 4px 18px', fontSize: 13, maxWidth: '80%' } }>Có rất nhiều chủ đề để bạn lựa chọn và những chủ đề này đều khác nhau đôi chút.</Box>
												</Box>
												<Box sx={ { display: 'flex', justifyContent: 'flex-end' } }>
													<Box sx={ { bgcolor: pendingBubbleColor, color: '#fff', px: 1.5, py: 0.75, borderRadius: '18px 18px 4px 18px', fontSize: 13, maxWidth: '80%' } }>Tin nhắn mà bạn gửi cho người khác sẽ có màu này.</Box>
												</Box>
												<Box sx={ { display: 'flex', justifyContent: 'flex-start' } }>
													<Box sx={ { bgcolor: '#e9e9eb', color: '#111', px: 1.5, py: 0.75, borderRadius: '18px 18px 18px 4px', fontSize: 13, maxWidth: '80%' } }>Tin nhắn của bạn bè sẽ tương tự như thế này.</Box>
												</Box>
												<Typography sx={ { fontSize: 10, textAlign: 'center', color: pendingBgColor === '#1a1a2e' ? '#aaa' : '#bbb' } }>09:45</Typography>
												<Box sx={ { display: 'flex', justifyContent: 'flex-start' } }>
													<Box sx={ { bgcolor: '#e9e9eb', color: '#111', px: 1.5, py: 0.75, borderRadius: '18px 18px 18px 4px', fontSize: 13, maxWidth: '80%' } }>Nhấp vào Chọn để chọn chủ đề này.</Box>
												</Box>
											</Box>
										</Box>
									</DialogContent>
									<DialogActions sx={ { px: 2.5, py: 1.5, borderTop: '1px solid #eee', gap: 1.5 } }>
										<Button onClick={ () => setThemeModalOpen(false) } variant="outlined" fullWidth sx={ { borderRadius: 2 } }>Hủy</Button>
										<Button onClick={ async () => {
											const BG_NAMES = { '#ffffff': 'Mặc định', '#e3f2fd': 'Bầu trời', '#e8f5e9': 'Thảo nguyên', '#fce4ec': 'Hoàng hôn', '#fff8e1': 'Nắng vui', '#f3e5f5': 'Lavender', '#1a1a2e': 'Xuyên đêm', '#fff3e0': 'Dương nắng', '#f0f4f8': 'Sương sớm', '#e0f7fa': 'Bãi biển', '#fbe9e7': 'Đất nung', '#fafafa': 'Kem dừa' };
											// Apply locally first
											if (themeTab === 0) {
												if (pendingBgFile) {
													const localUrl = pendingBgPreview;
													setChatBgColor(localUrl ? `url(${ localUrl })` : '#ffffff');
												} else {
													setChatBgColor(pendingBgColor);
												}
											} else {
												setOwnBubbleColor(pendingBubbleColor);
											}
											setThemeModalOpen(false);
											if (activeChatConv) {
												try {
													const payload = {};
													if (themeTab === 0) {
														if (pendingBgFile) {
															payload.bgImageFile = pendingBgFile;
															payload.themeName = 'ảnh tùy chỉnh';
														} else {
															payload.theme = pendingBgColor === '#ffffff' ? null : pendingBgColor;
															payload.themeName = BG_NAMES[pendingBgColor] || 'chủ đề mới';
														}
													} else {
														payload.bubbleColor = pendingBubbleColor;
													}
													const res = await chatService.updateTheme(activeChatConv._id, payload);
													// If image upload: server returns real URL, update bg
													if (pendingBgFile && res.data?.theme) {
														setChatBgColor(`url(${ CHAT_API_BASE_MODAL }${ res.data.theme })`);
													}
													if (res.data?.message) {
														setChatMessages(prev => prev.some(m => m._id === res.data.message._id) ? prev : [...prev, res.data.message]);
													}
												} catch (e) { }
											}
										} } variant="contained" fullWidth sx={ { borderRadius: 2 } }>Chọn</Button>
									</DialogActions>
								</Dialog>
							);
						})() }

						{/* Bubble color picker popover */ }
						<Popover open={ false } onClose={ () => { } } anchorReference="anchorPosition" anchorPosition={ { top: 0, left: 0 } } />

						{/* Messages */ }
						<Box
							ref={ chatScrollRef }
							onScroll={ e => { if (e.target.scrollTop === 0 && chatHasMore && !chatLoadingMore) handleLoadMoreMessages(); } }
							sx={ {
								flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.6,
								background: chatBgColor.startsWith('url(') ? `${ chatBgColor } center/cover no-repeat` : chatBgColor,
							} }
						>
							{ chatLoadingMore && <Box sx={ { display: 'flex', justifyContent: 'center', py: 0.5 } }><CircularProgress size={ 18 } /></Box> }
							<Box sx={ { flex: 1 } } />
							{ chatMessages.length === 0 && !chatLoadingMore && (
								<Box sx={ { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 1, py: 3 } }>
									<Avatar src={ getAvatarUrl(other?.avatar) } sx={ { width: 56, height: 56, fontSize: 22 } }>
										{ other?.fullName?.charAt(0) || other?.username?.charAt(0) }
									</Avatar>
									<Typography fontWeight={ 700 } fontSize={ 14 }>{ other?.fullName || other?.username }</Typography>
									<Typography fontSize={ 12 } color="text.secondary" textAlign="center" sx={ { px: 2 } }>Hãy gửi tin nhắn đầu tiên để bắt đầu cuộc trò chuyện!</Typography>
								</Box>
							) }
							{ (() => {
								const msgCurrentUserId = (user?.id || user?._id)?.toString();
								let lastDateLabel = null;
								return chatMessages.map((msg, idx) => {
									if (msg.type === 'system') {
										return (
											<Box key={ msg._id || idx } sx={ { display: 'flex', justifyContent: 'center', my: 0.5 } }>
												<Typography sx={ { fontSize: 11, color: '#888', bgcolor: otherBubbleColor, px: 1.5, py: 0.4, borderRadius: 3, textAlign: 'center', maxWidth: '85%' } }>
													{ msg.content }
												</Typography>
											</Box>
										);
									} const isOwn = msg.sender?._id?.toString() === msgCurrentUserId || msg.sender?.toString() === msgCurrentUserId;
									const dateLabel = msg.createdAt ? formatDateLabel(msg.createdAt) : null;
									const showDateSep = dateLabel && dateLabel !== lastDateLabel;
									if (showDateSep) lastDateLabel = dateLabel;
									const isLast = idx === chatMessages.length - 1;
									const att = msg.attachments?.length > 0 ? msg.attachments[0] : null;
									return (
										<React.Fragment key={ msg._id || idx }>
											{ showDateSep && (
												<Box sx={ { display: 'flex', justifyContent: 'center', my: 0.5 } }>
													<Typography sx={ { fontSize: 11, color: '#aaa', bgcolor: datePillColor, px: 1.5, py: 0.25, borderRadius: 3 } }>{ dateLabel }</Typography>
												</Box>
											) }
											<Box
												sx={ { display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', alignItems: 'center', gap: 0.5 } }
												onMouseEnter={ e => { e.currentTarget.querySelectorAll('.msg-action-btn').forEach(b => b.style.opacity = '1'); } }
												onMouseLeave={ e => { e.currentTarget.querySelectorAll('.msg-action-btn').forEach(b => b.style.opacity = '0'); } }
											>
												{ !isOwn && <Avatar src={ getAvatarUrl(other?.avatar) } sx={ { width: 22, height: 22, fontSize: 10 } }>{ other?.fullName?.charAt(0) }</Avatar> }
												{ isOwn && !msg.recalled && (
													<>
														<Tooltip title="Thu hồi">
															<IconButton className="msg-action-btn" size="small" onClick={ async () => { try { await chatService.recallMessage(activeChatConv._id, msg._id); setChatMessages(prev => prev.map(m => m._id === msg._id ? { ...m, recalled: true } : m)); } catch (e) { } } }
																sx={ { opacity: 0, transition: 'opacity 0.15s', p: 0.5, color: '#888', '&:hover': { color: '#d32f2f' } } }>
																<DeleteOutlineIcon sx={ { fontSize: 17 } } />
															</IconButton>
														</Tooltip>
														<Tooltip title="Trả lời">
															<IconButton className="msg-action-btn reply-btn" size="small" onClick={ () => setReplyingTo(msg) }
																sx={ { opacity: 0, transition: 'opacity 0.15s', p: 0.5, color: '#888', '&:hover': { color: '#333' } } }>
																<ReplyIcon sx={ { fontSize: 18 } } />
															</IconButton>
														</Tooltip>
													</>
												) }
												<Box sx={ { display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start', gap: 0.2, maxWidth: '72%' } }>
													{ msg.replyTo && (
														<Box sx={ { bgcolor: otherBubbleColor, borderLeft: '3px solid #bbb', px: 1, py: 0.5, borderRadius: 1, fontSize: 11, maxWidth: '100%', overflow: 'hidden' } }>
															<Typography sx={ { fontSize: 10, fontWeight: 600, color: '#888' } }>{ msg.replyTo.sender?.fullName || 'Tin nhắn' }</Typography>
															{ msg.replyTo.recalled ? (
																<Typography sx={ { fontSize: 11, color: '#aaa', fontStyle: 'italic' } }>Tin nhắn đã được thu hồi</Typography>
															) : (() => {
																const rAtt = msg.replyTo.attachments?.[0]; return rAtt?.type === 'image' ? (
																	<Box component="img" src={ `${ CHAT_API_BASE }${ rAtt.url }` } alt={ rAtt.name }
																		onClick={ (e) => { e.stopPropagation(); setImagePreview(`${ CHAT_API_BASE }${ rAtt.url }`); } }
																		sx={ { maxHeight: 60, maxWidth: 100, borderRadius: 1, display: 'block', mt: 0.3, cursor: 'zoom-in' } } />
																) : rAtt?.type === 'file' ? (
																	<Box sx={ { display: 'flex', alignItems: 'center', gap: 0.5 } }>
																		<AttachFileIcon sx={ { fontSize: 11, color: 'text.secondary' } } />
																		<Typography sx={ { fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }>{ rAtt.name }</Typography>
																	</Box>
																) : (
																	<Typography sx={ { fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }>{ msg.replyTo.content }</Typography>
																);
															})() }
														</Box>
													) }
													{ msg.recalled ? (
														<Box sx={ { bgcolor: isOwn ? ownBubbleColor : otherBubbleColor, color: isOwn ? 'rgba(255,255,255,0.6)' : '#aaa', px: 1.5, py: 0.75, borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px', fontSize: 12.5, fontStyle: 'italic' } }>
															Tin nhắn đã được thu hồi
														</Box>
													) : att?.type === 'image' ? (
														<Box component="img" src={ `${ CHAT_API_BASE }${ att.url }` } alt={ att.name }
															onClick={ () => setImagePreview(`${ CHAT_API_BASE }${ att.url }`) }
															sx={ { maxWidth: 210, borderRadius: 2, display: 'block', cursor: 'zoom-in' } } />
													) : (
														<Box sx={ { bgcolor: isOwn ? ownBubbleColor : otherBubbleColor, color: isOwn ? '#fff' : '#111', px: 1.5, py: 0.75, borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px', fontSize: 13.5, lineHeight: 1.4, wordBreak: 'break-word' } }>
															{ att?.type === 'file' ? (
																<Box component="a" href={ `${ CHAT_API_BASE }${ att.url }` } target="_blank" rel="noreferrer" sx={ { display: 'flex', alignItems: 'center', gap: 0.5, color: 'inherit', textDecoration: 'none' } }>
																	<AttachFileIcon sx={ { fontSize: 14 } } />
																	<Typography sx={ { fontSize: 12, textDecoration: 'underline' } }>{ att.name }</Typography>
																</Box>
															) : msg.content }
														</Box>
													) }
													{ isLast && msg.createdAt && (
														<Typography sx={ { fontSize: 10, color: '#aaa', px: 0.5 } }>{ formatTime(msg.createdAt) }</Typography>
													) }
												</Box>
												{ !isOwn && !msg.recalled && (
													<Tooltip title="Trả lời">
														<IconButton className="msg-action-btn reply-btn" size="small" onClick={ () => setReplyingTo(msg) }
															sx={ { opacity: 0, transition: 'opacity 0.15s', p: 0.5, color: '#888', '&:hover': { color: '#333' } } }>
															<ReplyIcon sx={ { fontSize: 18 } } />
														</IconButton>
													</Tooltip>
												) }
											</Box>
										</React.Fragment>
									);
								});
							})() }
							<div ref={ chatEndRef } />
						</Box>

						{/* Reply bar */ }
						{ replyingTo && (
							<Box sx={ { px: 1.5, py: 0.75, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 } }>
								<ReplyIcon sx={ { fontSize: 14, color: 'text.secondary' } } />
								<Box sx={ { flex: 1, minWidth: 0 } }>
									<Typography sx={ { fontSize: 10, fontWeight: 600, color: 'text.secondary' } }>{ replyingTo.sender?.fullName || 'Tin nhắn' }</Typography>
									{ (() => {
										const rAtt = replyingTo.attachments?.[0]; return rAtt?.type === 'image' ? (
											<Box sx={ { display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 } }>
												<Box component="img" src={ `${ CHAT_API_BASE }${ rAtt.url }` } alt={ rAtt.name } sx={ { height: 32, width: 32, objectFit: 'cover', borderRadius: 0.5 } } />
												<Typography sx={ { fontSize: 11, color: '#555' } }>Hình ảnh</Typography>
											</Box>
										) : rAtt?.type === 'file' ? (
											<Box sx={ { display: 'flex', alignItems: 'center', gap: 0.5 } }>
												<AttachFileIcon sx={ { fontSize: 11, color: 'text.secondary' } } />
												<Typography sx={ { fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }>{ rAtt.name }</Typography>
											</Box>
										) : (
											<Typography sx={ { fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }>{ replyingTo.content }</Typography>
										);
									})() }
								</Box>
								<IconButton size="small" onClick={ () => setReplyingTo(null) } sx={ { p: 0.3 } }><CloseIcon sx={ { fontSize: 14 } } /></IconButton>
							</Box>
						) }

						{/* Input */ }
						<Box sx={ { px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'background.paper' } }>
<input ref={ fileInputRef } type="file" style={ { position: 'fixed', top: '-100vh', left: '-100vw', width: '1px', height: '1px', opacity: 0 } } onChange={ () => { const f = fileInputRef.current?.files?.[0]; console.log('[Chat] File chọn:', f ? { name: f.name, size: f.size, type: f.type } : null); if (f) handleChatSend(); } } />

								<IconButton size="small" onClick={ () => { if (fileInputRef.current) fileInputRef.current.click(); } } sx={ { color: '#888', p: 0.5 } }>
									<ImageIcon sx={ { fontSize: 18 } } />
								</IconButton>


								<IconButton size="small" onClick={ () => { if (fileInputRef.current) fileInputRef.current.click(); } } sx={ { color: '#888', p: 0.5 } }>
								<AttachFileIcon sx={ { fontSize: 18 } } />
							</IconButton>

							<InputBase placeholder="Aa" value={ chatInput } onChange={ e => setChatInput(e.target.value) }
								onKeyDown={ e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } } }
								sx={ { flex: 1, fontSize: 14, bgcolor: 'action.hover', borderRadius: 4, px: 1.5, py: 0.5, color: 'text.primary' } }
								multiline maxRows={ 3 }
							/>
							<IconButton size="small" onClick={ handleChatSend } disabled={ (!chatInput.trim() && !fileInputRef.current?.files?.[0]) || chatSending } sx={ { color: ownBubbleColor, '&:hover': { bgcolor: '#e8f4ff' } } }>
								<SendIcon fontSize="small" />
							</IconButton>
						</Box>
					</Paper>
				);
			})() }
			<AdminProfileModal open={ profileOpen } onClose={ () => setProfileOpen(false) } />
			<Dialog
				open={ logoutConfirmOpen }
				onClose={ () => setLogoutConfirmOpen(false) }
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Xác nhận đăng xuất</DialogTitle>
				<DialogContent>
					<Typography variant="body2">Bạn có chắc muốn đăng xuất khỏi tài khoản?</Typography>
				</DialogContent>
				<DialogActions sx={ { px: 3, pb: 2 } }>
					<Button onClick={ () => setLogoutConfirmOpen(false) }>Hủy</Button>
					<Button variant="contained" color="error" onClick={ confirmLogout }>Đăng xuất</Button>
				</DialogActions>
			</Dialog>
			<ImagePreviewModal
				open={ !!imagePreview }
				src={ imagePreview }
				onClose={ () => setImagePreview(null) }
			/>
		</Box>
	);
}

export default AdminLayout;
